import { Component } from '@angular/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { CoreProvider } from './services/core.provider';
import { EventType } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent {
  public showMenu$ = new BehaviorSubject<boolean>(false);

  /**
   * The sidebar is always present; this only says whether it is showing labels
   * or just icons. hai-bench hid it entirely below 1200px, which meant the main
   * navigation vanished on a laptop — collapsing to a strip keeps it reachable
   * at any width. The choice is remembered.
   */
  public readonly collapsed$ = new BehaviorSubject<boolean>(localStorage.getItem(MENU_COLLAPSED_KEY) === 'true');

  /** Narrow viewports start collapsed, but the user can still expand them. */
  private readonly narrowViewportQuery = window.matchMedia('(max-width: 900px)');
  /**
   * Grouped rather than one flat list. Four unrelated links read as a pile;
   * named groups tell you where things live, and the grouping survives
   * collapsing because the icons keep their order.
   */
  private readonly userSections: MenuSection[] = [
    {
      // Marked, not translated here: these are built once at construction, and
      // a string resolved then would keep the language the app started in. The
      // template runs each `title` through `translate`.
      title: _('Matches'),
      pages: [
        {
          title: _('My matches'),
          url: '/videos/my-videos',
          icon: 'file-tray-stacked',
          // The one number worth interrupting for: a match sitting in
          // `awaiting_input` is holding a processing instance until it times out.
          badge: () => this.core.matchVideos.videos$.value.filter((v) => v.status === 'awaiting_input').length,
        },
      ],
    },
    {
      title: _('Settings'),
      pages: [
        { title: _('Tags'), url: '/configuration/tags', icon: 'apps' },
        { title: _('Profile'), url: '/configuration/user-profile', icon: 'person' },
      ],
    },
  ];

  /** Last, and only for SUPER_ADMIN: these are ours, not the customer's. */
  private readonly superadminSections: MenuSection[] = [
    ...this.userSections,
    {
      title: _('Admin'),
      pages: [
        { title: _('Fleet'), url: '/superadmin/fleet', icon: 'hardware-chip' },
        { title: _('Users'), url: '/superadmin/users', icon: 'people' },
      ],
    },
  ];

  /**
   * A getter rather than a field, because the role is not known when the
   * component is constructed — `initCore()` parses the token afterwards — and it
   * changes again on login, logout and impersonation. Both arrays are built
   * once, so this allocates nothing per change detection.
   *
   * Hiding the section is presentation only; `requireSuperadmin` guards the
   * route and the API guards the endpoints.
   */
  public get menuSections(): MenuSection[] {
    return this.core.auth.isSuperadmin ? this.superadminSections : this.userSections;
  }

  /**
   * Pages that take over the whole viewport. The review screen is one: the
   * player and the event list already fill it, and a sidebar next to them
   * leaves the video too small to judge a cut by.
   */
  private readonly menuDisabledStartUrls = [
    '/login',
    '/payment-confirmation',
  ];

  constructor(
    public readonly core: CoreProvider,
  ) {
    if (this.narrowViewportQuery.matches && localStorage.getItem(MENU_COLLAPSED_KEY) === null) {
      this.collapsed$.next(true);
    }
    this.narrowViewportQuery.addEventListener('change', (event) => {
      // Only nudges the default; an explicit choice always wins.
      if (localStorage.getItem(MENU_COLLAPSED_KEY) === null) this.collapsed$.next(event.matches);
    });

    this.core.router.events.subscribe((routerEvent) => {
      if (routerEvent.type == EventType.NavigationEnd) {
        console.debug('Router Event:', routerEvent);

        const isDisableMenuPage = this.menuDisabledStartUrls.some((urlStart) =>
          routerEvent.urlAfterRedirects.startsWith(urlStart)
        );
        const shouldShowMenu = !isDisableMenuPage;
        this.showMenu$.next(shouldShowMenu);
      }
    });

    // this.core.appIsReady$.subscribe((isReady) => {
    //   if (isReady) {
    //     this.core.auth.login({
    //       email: 'avecrem@arsahosting.com',
    //       otpSecret: '123456',
    //     });
    //   }
    // });
  }

  /** Collapse to icons, or expand back to labels. Remembered across sessions. */
  public toggleCollapsed(): void {
    const next = !this.collapsed$.value;
    this.collapsed$.next(next);
    localStorage.setItem(MENU_COLLAPSED_KEY, `${next}`);
  }

  public onMenuItemClick(): void {
    // Nothing to close: the sidebar is permanent now.
  }

  /** Activates the downloaded build and reloads. Declines while an upload is running. */
  public updateApp(): void {
    void this.core.versionService.update();
  }
}

/** Where the collapsed choice is remembered. */
const MENU_COLLAPSED_KEY = 'menu-collapsed';

type MenuSection = {
  title: string;
  pages: { title: string; url: string; icon: string; badge?: () => number }[];
};
